import { useEffect, useState, useCallback } from "react";

/**
 * beforeinstallprompt hook — safe cross-browser install-affordance helper.
 *
 * Chromium (desktop Chrome, Edge, most Android browsers) fires the
 * beforeinstallprompt event. We capture it (calling preventDefault so the
 * browser's own auto-mini-info-bar stays quiet) and expose:
 *   - canPrompt: true only while we currently hold a live prompt object
 *   - prompt():  fires the browser's real install prompt when we can
 *   - isIOS:     Safari iOS / iPadOS have no such API; consumers show the
 *                manual "Share -> Add to Home Screen" instructions instead
 *
 * Once the user accepts / dismisses / another install happens, the browser
 * invalidates the deferred event. We drop it and canPrompt goes back to
 * false — no zombie install button.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(false);

  useEffect(() => {
    const onBefore = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBefore as EventListener);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore as EventListener);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const prompt = useCallback(async () => {
    if (!deferred) return false;
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch {
      // Browser may reject if it decides the moment isn't installable
      // anymore. Either way the deferred event is single-use.
    }
    setDeferred(null);
    return true;
  }, [deferred]);

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)")?.matches ||
      (window.navigator as any).standalone === true);

  // Rough iOS detection — used ONLY to switch which instructions we show.
  // Never used for security decisions.
  const isIOS =
    typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !/crios|fxios|edgios/i.test(navigator.userAgent);

  return {
    canPrompt: !!deferred && !installed && !isStandalone,
    prompt,
    isIOS,
    isStandalone,
    installed,
  };
}
